import { Box, Tooltip, Typography } from "@mui/material";
import { Moment } from "moment";
import {
  CALENDAR_DATE_FORMAT,
  JOB_STATUS_COLORS,
  JOB_STATUS_LABELS,
} from "../../../../constants";
import { JobStatus } from "../../../../enums";
import { JobModel } from "../../../../models";
import { CalendarResourceRow, Job } from "../../../../types";
import { CalendarDayHeader } from "./CalendarDayHeader";

const getGridColumns = (dayCount: number) =>
  `180px repeat(${dayCount}, minmax(110px, 1fr))`;

type ResourceJobItemProps = {
  job: Job;
  hasConflict: boolean;
  onClick: (job: Job) => void;
};

const ResourceJobItem = ({
  job,
  hasConflict,
  onClick,
}: ResourceJobItemProps) => (
  <Tooltip
    title={`${JobModel.getJobTitle(job)} · ${JobModel.getTimeRangeLabel(job)} · ${JOB_STATUS_LABELS[job.status]}${hasConflict ? " · Conflict" : ""}`}
  >
    <Box
      role="button"
      tabIndex={0}
      onClick={(event) => {
        event.stopPropagation();
        onClick(job);
      }}
      onKeyDown={(event) => event.key === "Enter" && onClick(job)}
      sx={{
        display: "flex",
        gap: 0.5,
        px: 0.75,
        py: 0.25,
        cursor: "pointer",
        borderRadius: "4px",
        border: `1px solid ${hasConflict ? "var(--dashboard-danger)" : "var(--dashboard-border-tile)"}`,
        borderLeft: `3px solid ${JOB_STATUS_COLORS[job.status]}`,
        backgroundColor: JobModel.isEvent(job)
          ? "var(--dashboard-track-segmented)"
          : "var(--dashboard-surface)",
        opacity: job.status === JobStatus.Canceled ? 0.6 : 1,
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
        {JobModel.getTimeLabel(job.startTime)}
      </Typography>
      <Typography noWrap sx={{ fontSize: 11 }}>
        {JobModel.getJobTitle(job)}
      </Typography>
    </Box>
  </Tooltip>
);

type ResourceWeekViewProps = {
  days: Moment[];
  today: string;
  rows: CalendarResourceRow[];
  resourceLabel: string;
  emptyMessage: string;
  conflictJobIds: Set<number>;
  onCellClick: (date: Moment, row: CalendarResourceRow) => void;
  onJobClick: (job: Job) => void;
  onDayClick?: (day: Moment) => void;
};

export const ResourceWeekView = ({
  days,
  today,
  rows,
  resourceLabel,
  emptyMessage,
  conflictJobIds,
  onCellClick,
  onJobClick,
  onDayClick,
}: ResourceWeekViewProps) => (
  <Box sx={{ overflowX: "auto" }}>
    <Box sx={{ minWidth: 950 }}>
      <CalendarDayHeader
        days={days}
        today={today}
        gridTemplateColumns={getGridColumns(days.length)}
        label={resourceLabel}
        onDayClick={onDayClick}
      />

      {rows.map((row) => (
        <Box
          key={row.id}
          sx={{
            display: "grid",
            gridTemplateColumns: getGridColumns(days.length),
            "&:not(:last-of-type)": {
              borderBottom: "1px solid var(--dashboard-border-row)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              px: 2,
              py: 1,
              minWidth: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--dashboard-text-primary)",
                }}
              >
                {row.title}
              </Typography>
              <Typography noWrap className="dashboard-helper-text">
                {row.subtitle}
              </Typography>
            </Box>
            <Tooltip title="Scheduled hours this week">
              <Typography
                className="dashboard-helper-text"
                sx={{ flexShrink: 0 }}
              >
                {row.scheduledHours}h
              </Typography>
            </Tooltip>
          </Box>

          {days.map((day) => {
            const cell = row.days[day.format(CALENDAR_DATE_FORMAT)];

            return (
              <Box
                key={day.format(CALENDAR_DATE_FORMAT)}
                title={cell.hoursLabel ? `Available ${cell.hoursLabel}` : ""}
                onClick={() => onCellClick(day, row)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: cell.blockedLabel ? "center" : "flex-start",
                  gap: 0.5,
                  minHeight: 56,
                  p: 0.75,
                  cursor: "pointer",
                  borderLeft: "1px solid var(--dashboard-border-row)",
                  backgroundColor: cell.blockedLabel
                    ? "var(--dashboard-track-segmented)"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: cell.blockedLabel
                      ? "var(--dashboard-track-segmented)"
                      : "var(--dashboard-background)",
                  },
                }}
              >
                {cell.blockedLabel && cell.jobs.length === 0 && (
                  <Typography
                    className="dashboard-helper-text"
                    sx={{ textAlign: "center" }}
                  >
                    {cell.blockedLabel}
                  </Typography>
                )}
                {cell.jobs.map((job) => (
                  <ResourceJobItem
                    key={job.id}
                    job={job}
                    hasConflict={conflictJobIds.has(job.id)}
                    onClick={onJobClick}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      ))}

      {rows.length === 0 && (
        <Typography className="dashboard-helper-text" sx={{ p: 3 }}>
          {emptyMessage}
        </Typography>
      )}
    </Box>
  </Box>
);
